// src/Pages/views/Invoices/InvoicePage.tsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Col,
  Container,
  FormGroup,
  Row,
  Badge,
  Button,
  Modal,
} from "react-bootstrap";
import Select from "react-select";
import Split from "react-split";
import { groupBy } from "lodash";
import {
  fetchInvoiceTls,
  setDocumentId,
  setTransactionLineRowNumber,
} from "src/redux/reducers/InvoiceManager";
import InvoiceContainer from "./InvoiceContainer";
import PresignedDocumentViewer from "../PdfViewer/PresignedDocumentViewer";
import { Formik } from "formik";
import { Invoice2 } from "src/docuclipper/DocuclipperTypes";
import SelectWithErrorBadge from "../DataArchive/TransactionManager/SelectWithErrorBadge";
import { ReduxState } from "src/redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InvoiceMatchManager } from "./InvoiceMatching/InvoiceMatchManager";
import SelectCreatable from "react-select/creatable";
import { tagItem, untagItem, getTags } from "src/docuclipper/api";
import { TagSelector } from "./TagSelector";
import { useLocation, useHistory } from "react-router-dom";
import InvoiceTour from "../DataArchive/TransactionManager/InvoiceTour";
import { setHelpDrawerOpen } from "src/redux/reducers/HelpDrawer";
import HelpDrawer from "src/views/Docuclipper/Ratings/HelpDrawer";
import { setDownloadInvoiceModalOpen } from "src/redux/reducers/JobExport";

const InvoicePage: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const {
    documentId,
    transactionLineRowNumber,
    transactionLines,
    documents,
    loading,
    error,
    validationErrors,
    saving,
    lastSavedAt,
    saveError,
  } = useSelector((state: ReduxState) => state.InvoiceManager);

  // Tick once a second so "Saved Xs ago" stays accurate without re-saving.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const renderSaveIndicator = () => {
    if (saveError) {
      return (
        <span className="text-danger small mr-3">
          <FontAwesomeIcon icon="exclamation-triangle" className="mr-1" />
          Save failed
        </span>
      );
    }
    if (saving) {
      return (
        <span className="text-muted small mr-3">
          <FontAwesomeIcon icon="sync" className="mr-1 fa-spin" />
          Saving…
        </span>
      );
    }
    if (lastSavedAt) {
      const secs = Math.max(0, Math.floor((now - lastSavedAt) / 1000));
      const label =
        secs < 5
          ? "Saved just now"
          : secs < 60
            ? `Saved ${secs}s ago`
            : `Saved ${Math.floor(secs / 60)}m ago`;
      return (
        <span className="text-muted small mr-3">
          <FontAwesomeIcon icon="check" className="mr-1 text-success" />
          {label}
        </span>
      );
    }
    return null;
  };

  const [showMatchModal, setShowMatchModal] = useState(false);

  const PDF_PANEL_COLLAPSED_KEY = "docuclipper.invoice.pdfPanelCollapsed";
  const [pdfPanelCollapsed, setPdfPanelCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PDF_PANEL_COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        PDF_PANEL_COLLAPSED_KEY,
        pdfPanelCollapsed ? "1" : "0"
      );
    } catch {
      /* ignore */
    }
  }, [pdfPanelCollapsed]);

  const params = new URLSearchParams(location.search);
  const invoiceId = params.get("invoiceId");

  useEffect(() => {
    dispatch(fetchInvoiceTls(invoiceId));
  }, [location.search]);

  const tlsByDocId = groupBy(transactionLines || [], "documentId");

  const rowOptions = React.useMemo(() => {
    const currentDocId = documentId || "";
    const currentTls = tlsByDocId[currentDocId] || [];

    return currentTls
      .sort((a, b) => (a.pageNumber || 0) - (b.pageNumber || 0))
      .map((tl, index) => ({
        label: `${index + 1}`,
        value: index,
        hasWarning: validationErrors?.[tl.id || ""]?.valid === false || false,
      }));
  }, [documentId, tlsByDocId, validationErrors]);

  const transactionLine = React.useMemo(() => {
    if (!documentId) return null;
    return (
      transactionLines?.find(
        (tl) =>
          tl.documentId === documentId &&
          tl.rowNumber === transactionLineRowNumber
      ) || null
    );
  }, [documentId, transactionLines, transactionLineRowNumber]);

  // Get page number from transaction line for potential future use
  const pageNumber = transactionLine ? transactionLine.pageNumber : 0;

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const rowSelectValue = rowOptions.find(
    (x) => x.value === transactionLineRowNumber
  );

  const documentOptions = documents
    .map((doc) => {
      const hasValidationWarning = transactionLines.some(
        (tl) =>
          tl.documentId.toString() === doc.id.toString() &&
          validationErrors?.[tl.id || ""] &&
          !validationErrors?.[tl.id || ""].valid
      );
      return {
        label: doc.originalname,
        value: doc.id,
        // Amber warning triangle fires on any of: validation errors,
        // missing critical fields (date/total), or zero extracted rows
        hasWarning:
          hasValidationWarning ||
          doc.hasPartialExtraction === true ||
          doc.hasNoTransactions === true,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const documentSelectValue = documentOptions.find(
    (x) => x.value?.toString() === documentId
  );

  const selectedDoc = documents.find(
    (d) => d.id?.toString() === documentId
  );
  const selectedHasNoTransactions = selectedDoc?.hasNoTransactions === true;
  const selectedHasPartialExtraction =
    !selectedHasNoTransactions && selectedDoc?.hasPartialExtraction === true;

  const handleDocumentChange = (option) => {
    dispatch(setDocumentId(option.value));
  };

  const handleRowChange = (option) => {
    dispatch(setTransactionLineRowNumber(option.value));
  };

  // Parse the currently-selected transaction line's row to surface the
  // duplicate-of signal written by the api finishInvoiceJob dedup pass.
  // We render the loud Alert before any per-field UI so it's the first
  // thing the user sees when they open a flagged invoice.
  let parsedInvoiceRow: Invoice2 | null = null;
  try {
    if (transactionLine?.row) {
      parsedInvoiceRow =
        typeof transactionLine.row === "string"
          ? (JSON.parse(transactionLine.row as any) as Invoice2)
          : (transactionLine.row as any);
    }
  } catch (e) {
    parsedInvoiceRow = null;
  }
  const dupOfTlId = parsedInvoiceRow?.duplicateOfTransactionLineId;

  const handleOpenDuplicateOriginal = () => {
    if (!dupOfTlId) return;
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("invoiceId", String(dupOfTlId));
    history.replace(`${location.pathname}?${searchParams.toString()}`);
  };

  // "Why" reasons for the dedup call. The api matches on vendor + total +
  // doc date, so surface those concrete values to let the user verify.
  const dupReasonParts: string[] = [];
  if (parsedInvoiceRow?.vendorName || parsedInvoiceRow?.vendor) {
    dupReasonParts.push(
      `vendor "${parsedInvoiceRow?.vendorName || parsedInvoiceRow?.vendor}"`
    );
  }
  if (parsedInvoiceRow?.total) {
    dupReasonParts.push(`total ${parsedInvoiceRow.total}`);
  }
  if (parsedInvoiceRow?.date) {
    dupReasonParts.push(`date ${parsedInvoiceRow.date}`);
  }

  return (
    <Container fluid>
      {dupOfTlId && (
        <Alert variant="warning" className="mb-3">
          <Alert.Heading style={{ fontSize: "1.1rem" }}>
            <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
            This invoice appears to be a duplicate
          </Alert.Heading>
          <p className="mb-2">
            The extractor flagged this as a duplicate of{" "}
            <Button
              variant="link"
              className="p-0 align-baseline"
              onClick={handleOpenDuplicateOriginal}
            >
              invoice #{dupOfTlId}
            </Button>
            .
          </p>
          {dupReasonParts.length > 0 && (
            <p className="mb-0 small">
              <strong>Why:</strong> matching {dupReasonParts.join(", ")}.
              Verify before approving or syncing.
            </p>
          )}
        </Alert>
      )}
      {renderSaveIndicator() && (
        <Row className="mb-2">
          <Col className="d-flex justify-content-end align-items-center">
            {renderSaveIndicator()}
          </Col>
        </Row>
      )}

      <Split
        key={`invoice-${pdfPanelCollapsed ? "c" : "e"}`}
        className="split"
        sizes={pdfPanelCollapsed ? [0, 100] : [40, 60]}
        minSize={pdfPanelCollapsed ? 0 : 100}
        gutterSize={pdfPanelCollapsed ? 0 : 10}
      >
        {pdfPanelCollapsed ? (
          <div style={{ width: 0, height: "100%" }} />
        ) : (
          <div
            className="d-flex flex-column pdf-pane"
            style={{ position: "relative", overflow: "hidden" }}
          >
            <button
              type="button"
              onClick={() => setPdfPanelCollapsed(true)}
              title="Hide PDF"
              aria-label="Hide PDF"
              style={{
                alignSelf: "flex-end",
                marginBottom: 4,
                border: "1px solid #d0d7de",
                background: "#fff",
                borderRadius: 4,
                padding: "2px 8px",
                fontSize: 12,
                color: "#57606a",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <FontAwesomeIcon icon="chevron-left" />
              <span>Hide PDF</span>
            </button>
            {!invoiceId && (
              <Row className="mb-2 mr-1 invoice-tour-select-doc">
                <Col md="8">
                  <FormGroup>
                    <strong>Document</strong>
                    <SelectWithErrorBadge
                      value={documentSelectValue || null}
                      onChange={handleDocumentChange}
                      options={documentOptions}
                    />
                    {selectedHasNoTransactions && (
                      <Alert variant="info" className="mt-2 mb-0">
                        <strong>No invoice data was extracted from this document.</strong>{" "}
                        The pipeline ran successfully but couldn't find any invoice
                        rows — the document may not contain a recognizable invoice,
                        or extraction failed on every page.
                      </Alert>
                    )}
                    {selectedHasPartialExtraction && (
                      <Alert variant="warning" className="mt-2 mb-0">
                        <strong>Some fields couldn't be extracted.</strong> The
                        date or total is missing on one or more invoice rows —
                        please review and fill them in before exporting.
                      </Alert>
                    )}
                  </FormGroup>
                </Col>
                <Col md="4">
                  {documentId && (
                    <FormGroup>
                      <strong>Invoice</strong>
                      <SelectWithErrorBadge
                        value={rowSelectValue || null}
                        onChange={handleRowChange}
                        options={rowOptions}
                      />
                    </FormGroup>
                  )}
                </Col>
              </Row>
            )}
            <Row className="mr-1">
              <Col md="12">
                {documentId && (
                  <PresignedDocumentViewer
                    key={`${documentId}-${pageNumber}-${invoiceId}`}
                    documentId={documentId.toString()}
                    pageNumber={pageNumber > 0 ? pageNumber + 1 : undefined}
                  />
                )}
              </Col>
            </Row>
          </div>
        )}

        <div className="ml-1" style={{ position: "relative" }}>
          {pdfPanelCollapsed && (
            <div className="d-flex justify-content-start mb-2">
              <button
                type="button"
                onClick={() => setPdfPanelCollapsed(false)}
                title="Show PDF"
                aria-label="Show PDF"
                style={{
                  border: "1px solid #d0d7de",
                  background: "#fff",
                  borderRadius: 4,
                  padding: "4px 10px",
                  fontSize: 12,
                  color: "#57606a",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FontAwesomeIcon icon="chevron-right" />
                <span>Show PDF</span>
              </button>
            </div>
          )}
          {pdfPanelCollapsed && !invoiceId && (
            <Row className="mb-2 mr-1 invoice-tour-select-doc">
              <Col md="8">
                <FormGroup>
                  <strong>Document</strong>
                  <SelectWithErrorBadge
                    value={documentSelectValue || null}
                    onChange={handleDocumentChange}
                    options={documentOptions}
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                {documentId && (
                  <FormGroup>
                    <strong>Invoice</strong>
                    <SelectWithErrorBadge
                      value={rowSelectValue || null}
                      onChange={handleRowChange}
                      options={rowOptions}
                    />
                  </FormGroup>
                )}
              </Col>
            </Row>
          )}
          <Row>
            <Col>
              <InvoiceContainer />
            </Col>
          </Row>
        </div>
      </Split>

      <Modal
        show={showMatchModal}
        onHide={() => setShowMatchModal(false)}
        size="lg"
      >
        <Modal.Header
          closeButton
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
          placeholder=""
        >
          <Modal.Title>Match Rules</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InvoiceMatchManager
            transactionLineId={Number(transactionLine?.id)}
            onMatchCreated={() => {
              setShowMatchModal(false);
            }}
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default InvoicePage;
